import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { axiosInstance } from '../../../../helpers'
import type { PipelineDefinition } from './pipelineBuilder.types'

export const PROPERTY_PIPELINE_CONFIGS_KEY = 'property-pipeline-configs'
export const PROPERTY_PIPELINE_CONFIG_KEY = 'property-pipeline-config'
export const ORG_PIPELINE_CONFIGS_KEY = 'org-pipeline-configs'
export const ORG_LEADS_PIPELINE_CONFIGS_KEY = 'org-leads-pipeline-configs'
export const PIPELINE_CONFIG_TEMPLATES_KEY = 'pipeline-config-templates'

export const pipelineConfigTemplatesKey = () =>
    [PIPELINE_CONFIG_TEMPLATES_KEY] as const

export const propertyPipelineConfigsKey = (
    apartmentId: number | null | undefined,
) => [PROPERTY_PIPELINE_CONFIGS_KEY, apartmentId] as const

export const propertyPipelineConfigKey = (
    apartmentId: number | null | undefined,
    configId: number | null | undefined,
) => [PROPERTY_PIPELINE_CONFIG_KEY, apartmentId, configId] as const

export const orgPipelineConfigsKey = (
    organizationId: number | null | undefined,
) => [ORG_PIPELINE_CONFIGS_KEY, organizationId] as const

export const orgLeadsPipelineConfigsKey = (
    organizationId: number | null | undefined,
) => [ORG_LEADS_PIPELINE_CONFIGS_KEY, organizationId] as const

export const orgLeadsPipelineConfigKey = (
    organizationId: number | null | undefined,
    configId: number | null | undefined,
) => [ORG_LEADS_PIPELINE_CONFIGS_KEY, organizationId, configId] as const

export interface PipelineConfigTemplateListItem {
    key: string
    name: string
    description: string
    version: number
}

export interface PropertyPipelineConfigListItem {
    id: number
    name: string
    apartment_id: number
    apartment_name?: string
    is_editable: boolean
    copied_from_id?: number | null
    copied_from_name?: string | null
    created_from_org_pipeline_id?: number | null
    created_from_org_pipeline_name?: string | null
    created_from_season_id?: number | null
    created_from_season_name?: string | null
    created_from_template_key?: string | null
    created_from_template_version?: number | null
    created_date?: string
    modified_date?: string
}

export interface PropertyPipelineConfigDetail {
    id: number
    name: string
    apartment_id: number
    apartment_name?: string
    definition: PipelineDefinition
    is_editable: boolean
    copied_from_id?: number | null
    created_from_template_key?: string | null
    created_from_template_version?: number | null
    created_from_season_id?: number | null
    created_date?: string
    modified_date?: string
}

export interface PipelineConfigSource {
    id: number
    name: string
    apartment_id: number
}

export function usePropertyPipelineConfigs(
    apartmentId: number | null | undefined,
) {
    return useQuery({
        queryKey: propertyPipelineConfigsKey(apartmentId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<
                PropertyPipelineConfigListItem[]
            >(`/company/apartment/${apartmentId}/pipeline-configs/`)
            return data
        },
        enabled: apartmentId != null,
        refetchOnWindowFocus: false,
    })
}

export function usePipelineConfigTemplates() {
    return useQuery({
        queryKey: pipelineConfigTemplatesKey(),
        queryFn: async () => {
            const { data } = await axiosInstance.get<
                PipelineConfigTemplateListItem[]
            >('/company/pipeline-config-templates/')
            return data
        },
        refetchOnWindowFocus: false,
    })
}

export function useOrgPipelineConfigs(
    organizationId: number | null | undefined,
) {
    return useQuery({
        queryKey: orgPipelineConfigsKey(organizationId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<
                PropertyPipelineConfigListItem[]
            >(
                `/company/organization/${organizationId}/pipeline-configs/property-copies/`,
            )
            return data
        },
        enabled: organizationId != null,
        refetchOnWindowFocus: false,
    })
}

export function usePropertyPipelineConfig(
    apartmentId: number | null | undefined,
    configId: number | null | undefined,
) {
    return useQuery({
        queryKey: propertyPipelineConfigKey(apartmentId, configId),
        queryFn: async () => {
            const {
                data,
            } = await axiosInstance.get<PropertyPipelineConfigDetail>(
                `/company/apartment/${apartmentId}/pipeline-configs/${configId}/`,
            )
            return data
        },
        enabled: apartmentId != null && configId != null,
        refetchOnWindowFocus: false,
    })
}

export function usePipelineConfigSources(
    apartmentId: number | null | undefined,
    excludeConfigId: number | null | undefined,
) {
    return useQuery({
        queryKey: [
            PROPERTY_PIPELINE_CONFIGS_KEY,
            apartmentId,
            'sources',
            excludeConfigId,
        ],
        queryFn: async () => {
            const { data } = await axiosInstance.get<
                PropertyPipelineConfigListItem[]
            >(`/company/apartment/${apartmentId}/pipeline-configs/`)
            return data
                .filter((row) => row.id !== excludeConfigId)
                .map(
                    (row): PipelineConfigSource => ({
                        id: row.id,
                        name: row.name,
                        apartment_id: row.apartment_id,
                    }),
                )
        },
        enabled: apartmentId != null,
        refetchOnWindowFocus: false,
    })
}

export function useSavePropertyPipelineConfig(
    apartmentId: number | null | undefined,
    configId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (definition: PipelineDefinition) => {
            const {
                data,
            } = await axiosInstance.patch<PropertyPipelineConfigDetail>(
                `/company/apartment/${apartmentId}/pipeline-configs/${configId}/`,
                { definition },
            )
            return data
        },
        onSuccess: (data) => {
            queryClient.setQueryData(
                propertyPipelineConfigKey(apartmentId, configId),
                data,
            )
            queryClient.invalidateQueries({
                queryKey: propertyPipelineConfigsKey(apartmentId),
            })
            queryClient.invalidateQueries({
                queryKey: orgPipelineConfigsKey(undefined),
            })
        },
    })
}

export function useCreatePropertyPipelineConfig(
    apartmentId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            name: string
            template_key?: string
            definition?: PipelineDefinition
        }) => {
            const body: {
                name: string
                template_key?: string
                definition?: PipelineDefinition
            } = { name: payload.name }

            if (payload.template_key) {
                body.template_key = payload.template_key
            } else if (payload.definition) {
                body.definition = payload.definition
            }

            const {
                data,
            } = await axiosInstance.post<PropertyPipelineConfigDetail>(
                `/company/apartment/${apartmentId}/pipeline-configs/`,
                body,
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: propertyPipelineConfigsKey(apartmentId),
            })
            queryClient.invalidateQueries({
                queryKey: [ORG_PIPELINE_CONFIGS_KEY],
            })
        },
    })
}

export function useRenamePropertyPipelineConfig(
    apartmentId: number | null | undefined,
    configId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (name: string) => {
            const {
                data,
            } = await axiosInstance.patch<PropertyPipelineConfigDetail>(
                `/company/apartment/${apartmentId}/pipeline-configs/${configId}/`,
                { name },
            )
            return data
        },
        onSuccess: (data) => {
            queryClient.setQueryData(
                propertyPipelineConfigKey(apartmentId, configId),
                data,
            )
            queryClient.invalidateQueries({
                queryKey: propertyPipelineConfigsKey(apartmentId),
            })
        },
    })
}

export function useCopyPropertyPipelineConfig(
    apartmentId: number | null | undefined,
    organizationId?: number | null,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            sourceConfigId: number
            name?: string
            targetApartmentId?: number
            sourceApartmentId?: number
            createdFromSeasonId?: number
        }) => {
            const targetApartmentId = payload.targetApartmentId ?? apartmentId
            if (targetApartmentId == null) {
                throw new Error('Target apartment is required.')
            }

            if (
                organizationId != null &&
                payload.targetApartmentId != null &&
                payload.targetApartmentId !== apartmentId
            ) {
                const {
                    data,
                } = await axiosInstance.post<PropertyPipelineConfigDetail>(
                    `/company/organization/${organizationId}/pipeline-configs/copy/`,
                    {
                        source_config_id: payload.sourceConfigId,
                        target_apartment_id: payload.targetApartmentId,
                        name: payload.name,
                        created_from_season_id: payload.createdFromSeasonId,
                    },
                )
                return data
            }

            const sourceApartmentId = payload.sourceApartmentId ?? apartmentId
            if (sourceApartmentId == null) {
                throw new Error('Source apartment is required.')
            }

            const {
                data,
            } = await axiosInstance.post<PropertyPipelineConfigDetail>(
                `/company/apartment/${sourceApartmentId}/pipeline-configs/${payload.sourceConfigId}/copy/`,
                {
                    name: payload.name,
                    created_from_season_id: payload.createdFromSeasonId,
                },
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PROPERTY_PIPELINE_CONFIGS_KEY],
            })
            queryClient.invalidateQueries({
                queryKey: [ORG_PIPELINE_CONFIGS_KEY],
            })
        },
    })
}

export async function fetchPropertyPipelineDefinition(
    apartmentId: number,
    configId: number,
): Promise<PipelineDefinition | null> {
    const { data } = await axiosInstance.get<PropertyPipelineConfigDetail>(
        `/company/apartment/${apartmentId}/pipeline-configs/${configId}/`,
    )
    return data.definition
}
